import { ChronoUnit, LocalTime } from 'js-joda';
import { IIntent } from '@lifetools/shared-app-data';
import { toLocalTime } from '@lifetools/shared-utils-time';
import { TimeSlot } from 'types/TimeSlot';
import { ScheduledBlock } from 'types/ScheduledBlock';
import { LayoutConfig } from './LayoutConfig';
import { scheduleLength } from './scheduleLength';
import { StoredScheduleBlock } from './StoredScheduleBlock';

const minutesInDay = 24 * 60;

/**
 * A stored block resolved against its intent and placed on the slot grid.
 */
interface ResolvedEntry {
  intent: IIntent;
  offset: number;
  duration: number;
  isAppointment: boolean;
}

/**
 * A pinned appointment's occupancy interval, in minutes from the schedule start.
 */
interface Pin {
  start: number;
  end: number;
}

const roundUpToSlot = (minutes: number, interval: number): number =>
  Math.ceil(minutes / interval) * interval;

const buildTimes = (
  startTime: LocalTime,
  startOffset: number,
  lengthMinutes: number,
  interval: number,
): TimeSlot[] => {
  const times: TimeSlot[] = [];
  for (let offset = startOffset; offset < startOffset + lengthMinutes; offset += interval) {
    times.push({ offset, time: startTime.plusMinutes(offset) });
  }
  return times;
};

/**
 * THE single layout engine: renders the stored schedule model (`views.schedule.blocks`
 * plus the intents its blocks resolve to) into the `ScheduledBlock[]` the Scheduler
 * displays. Both the normal render and the drag preview go through this function, so
 * what a drag previews and what re-renders after its drop persists are the same
 * computation by construction.
 *
 * Placement walks the model in array order BY IDENTITY — never keyed by start — so
 * corrupt stored data degrades gracefully instead of silently dropping intents:
 *
 *  - Two blocks sharing a start: the first keeps it; later ones render at the nearest
 *    free slot after (falling back to before), with a warning. Nothing is dropped.
 *    (This subsumes the old render-side `repairDuplicateStarts`.)
 *  - A start off the slot grid snaps down to its containing slot, with a warning.
 *  - A start outside the schedule, or a schedule with no free slot left, skips the
 *    block from RENDER only — the stored model still carries it, so persisting an
 *    unrelated edit never deletes it.
 *  - A block whose todo hasn't loaded yet is skipped from render until it arrives.
 *
 * Appointments are pins: they always keep their stored position (duplicates
 * included), render their full duration, and never bump. Movable blocks lay out
 * around them:
 *
 *  - A movable block overlapping a pin keeps its start, renders only the slots
 *    before the pin, and is marked `scheduleAround` with the overflow as its
 *    `conflictDuration` (the wrap rule). The slots after the pin tile as empties.
 *  - A movable block running into the next movable block's start is truncated there
 *    and marked `nextIntent`; one running past the schedule end is truncated there
 *    and marked `scheduleEnd`. Conflicted overflow never displaces later blocks.
 *
 * Every slot not covered by a rendered block or pin tiles as a single-slot empty
 * block. Callers that hide empties (compact mode) filter them from the result.
 */
export function layoutSchedule(
  model: StoredScheduleBlock[],
  intentsByTodoId: Record<string, IIntent>,
  config: LayoutConfig,
): ScheduledBlock[] {
  const { startTime, interval } = config;

  // A non-positive interval would loop forever below; guard against corrupted data.
  if (!(interval > 0)) {
    console.error(
      `layoutSchedule: invalid interval ${interval}; expected a positive number. ` +
      `Returning an empty schedule to avoid an infinite loop.`
    );
    return [];
  }

  const endOffset = scheduleLength(config);

  // --- Resolve stored blocks against their intents and the slot grid. ---
  const entries: ResolvedEntry[] = [];

  for (const block of model) {
    const intent = intentsByTodoId[block.todo];

    // The todo hasn't loaded yet (or the block is orphaned): render nothing for it.
    // The block stays in the stored model, so persistence never drops it.
    if (!intent?.todo) {
      continue;
    }

    const start = (block.start != null ? toLocalTime(block.start) : undefined)
      ?? (intent.start != null ? toLocalTime(intent.start) : undefined);

    if (!start) {
      continue;
    }

    let offset = startTime.until(start, ChronoUnit.MINUTES);

    // Times after midnight sit "before" the start time; measure them forward.
    if (offset < 0) {
      offset += minutesInDay;
    }

    if (offset >= endOffset) {
      console.warn(
        `layoutSchedule: '${intent.todo.title}' starts at ${start} — outside the ` +
        `schedule; not rendering it (the stored model still carries it).`
      );
      continue;
    }

    if (offset % interval !== 0) {
      const snapped = Math.floor(offset / interval) * interval;
      console.warn(
        `layoutSchedule: '${intent.todo.title}' start ${start} is off the ` +
        `${interval}-minute slot grid; rendering it at ${startTime.plusMinutes(snapped)}.`
      );
      offset = snapped;
    }

    entries.push({
      intent,
      offset,
      duration: intent.duration ?? interval,
      isAppointment: intent.todo.type === 'appointment',
    });
  }

  // --- Claim start slots by identity; duplicates bump instead of collapsing. ---
  const appointments = entries.filter(entry => entry.isAppointment);
  const movables = entries.filter(entry => !entry.isAppointment);

  const claimed = new Set<number>();

  // Appointments are pinned: they claim their stored starts unconditionally (even
  // corrupt duplicates render in place rather than moving an appointment).
  for (const appointment of appointments) {
    claimed.add(appointment.offset);
  }

  const pins: Pin[] = appointments
    .map(appointment => ({
      start: appointment.offset,
      end: appointment.offset + roundUpToSlot(appointment.duration, interval),
    }))
    .sort((a, b) => a.start - b.start);

  const insidePin = (offset: number): boolean =>
    pins.some(pin => offset > pin.start && offset < pin.end);

  const placed: ResolvedEntry[] = [];
  const displaced: ResolvedEntry[] = [];

  for (const entry of movables) {
    if (claimed.has(entry.offset) || insidePin(entry.offset)) {
      displaced.push(entry);
    } else {
      claimed.add(entry.offset);
      placed.push(entry);
    }
  }

  for (const entry of displaced) {
    const isFree = (offset: number): boolean => !claimed.has(offset) && !insidePin(offset);
    let target: number | undefined;

    for (let offset = entry.offset + interval; offset < endOffset; offset += interval) {
      if (isFree(offset)) { target = offset; break; }
    }
    if (target === undefined) {
      for (let offset = entry.offset - interval; offset >= 0; offset -= interval) {
        if (isFree(offset)) { target = offset; break; }
      }
    }
    if (target === undefined) {
      console.warn(
        `layoutSchedule: no free slot for '${entry.intent.todo!.title}' (start ` +
        `occupied and the schedule is full); not rendering it.`
      );
      continue;
    }

    console.warn(
      `layoutSchedule: '${entry.intent.todo!.title}' shared start ` +
      `${startTime.plusMinutes(entry.offset)} with another block; rendering it at ` +
      `${startTime.plusMinutes(target)} instead so it isn't dropped.`
    );
    claimed.add(target);
    placed.push({ ...entry, offset: target });
  }

  // --- Lay out covers, conflicts (wrap included) and empties. ---
  const blocks: ScheduledBlock[] = [];
  const covered = new Set<number>();

  const markCovered = (offset: number, coverMinutes: number): void => {
    for (let m = 0; m < coverMinutes; m += interval) {
      covered.add(offset + m);
    }
  };

  const createBlock = (
    entry: ResolvedEntry,
    coverMinutes: number,
    conflictType?: 'nextIntent' | 'scheduleEnd' | 'scheduleAround',
    conflictDuration?: number,
  ): ScheduledBlock => {
    const start = startTime.plusMinutes(entry.offset);
    const block: ScheduledBlock = {
      start,
      startOffset: entry.offset,
      times: buildTimes(startTime, entry.offset, coverMinutes, interval),
      intent: { ...entry.intent, start },
    };

    if (conflictType) {
      block.conflictType = conflictType;
      block.conflictDuration = conflictDuration;
    }

    markCovered(entry.offset, coverMinutes);

    return block;
  };

  // Appointments render their full duration, clipped only by the schedule end.
  for (const appointment of appointments) {
    const rawEnd = appointment.offset + appointment.duration;
    const pastEnd = rawEnd > endOffset;
    const cover = Math.min(rawEnd, endOffset) - appointment.offset;

    blocks.push(pastEnd
      ? createBlock(appointment, cover, 'scheduleEnd', rawEnd - endOffset)
      : createBlock(appointment, cover));
  }

  const byOffset = [...placed].sort((a, b) => a.offset - b.offset);

  for (let i = 0; i < byOffset.length; i++) {
    const entry = byOffset[i];
    const rawEnd = entry.offset + entry.duration;
    const limit = Math.min(byOffset[i + 1]?.offset ?? Infinity, endOffset);

    // The first pin this block's duration crosses. A pin containing the block's
    // START can't occur (the claim pass bumps such blocks), so overlap always means
    // the pin starts strictly inside the block.
    const pin = pins.find(candidate => candidate.start > entry.offset && candidate.start < rawEnd);

    if (pin && pin.start <= limit) {
      // Wrap rule: keep the start, render only the slots before the pin, mark the
      // overflow. The slots after the pin tile as empties below.
      const fit = pin.start - entry.offset;
      blocks.push(createBlock(entry, fit, 'scheduleAround', entry.duration - fit));
    } else if (rawEnd > limit) {
      // Truncate at the next block / schedule end; the overflow is recorded as a
      // conflict instead of displacing anything after it.
      blocks.push(createBlock(
        entry,
        Math.max(0, limit - entry.offset),
        rawEnd > endOffset ? 'scheduleEnd' : 'nextIntent',
        rawEnd - limit,
      ));
    } else {
      blocks.push(createBlock(entry, entry.duration));
    }
  }

  // Tile every uncovered slot with a single-slot empty block.
  for (let offset = 0; offset < endOffset; offset += interval) {
    if (!covered.has(offset)) {
      blocks.push({
        start: startTime.plusMinutes(offset),
        startOffset: offset,
        times: buildTimes(startTime, offset, interval, interval),
      });
    }
  }

  return blocks.sort((a, b) => a.startOffset - b.startOffset);
}
